import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CallToAction } from '../components/CallToAction';
import { 
  ChevronDown,
  MessageSquare,
  Zap,
  Shield,
  Wallet,
  Settings,
  Bot
} from 'lucide-react';

const categories = [
  {
    id: 'general',
    name: 'General',
    icon: <MessageSquare className="w-5 h-5" />,
    questions: [
      {
        question: 'What is BOTINO.NET?',
        answer: 'BOTINO.NET is an automated cryptocurrency trading platform that allows you to create and manage trading bots without any programming knowledge. Our platform connects to popular crypto exchanges and supports TradingView integration for advanced trading strategies.'
      },
      {
        question: 'How do I get started?',
        answer: 'Getting started is easy! Simply sign up for a free account, connect your preferred crypto exchange using API keys, and choose or create your first trading strategy. Our platform guides you through each step of the process.'
      }
    ]
  },
  {
    id: 'trading',
    name: 'Trading',
    icon: <Zap className="w-5 h-5" />,
    questions: [
      {
        question: 'Which exchanges do you support?',
        answer: 'We support all major cryptocurrency exchanges including Binance, Coinbase Pro, Kraken, Bitfinex, and many more. We continuously add support for additional exchanges based on user demand and market relevance.'
      },
      {
        question: 'What trading pairs are available?',
        answer: 'You can trade any cryptocurrency pair that is available on your connected exchange. This includes major pairs like BTC/USDT, ETH/USDT, as well as other altcoin pairs.'
      }
    ]
  },
  {
    id: 'security',
    name: 'Security',
    icon: <Shield className="w-5 h-5" />,
    questions: [
      {
        question: 'How secure is BOTINO.NET?',
        answer: 'Security is our top priority. We use bank-grade encryption for all sensitive data and API keys. Your exchange API keys are stored with end-to-end encryption, and we never have withdrawal access to your funds. We also employ regular security audits and two-factor authentication.'
      },
      {
        question: 'What happens to my API keys?',
        answer: 'Your API keys are encrypted using military-grade encryption before being stored in our secure database. We recommend only providing trading-only API keys without withdrawal permissions for maximum security.'
      }
    ]
  },
  {
    id: 'billing',
    name: 'Billing',
    icon: <Wallet className="w-5 h-5" />,
    questions: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept credit/debit cards, PayPal, and various cryptocurrencies for payment. All payments are processed securely through our payment providers.'
      },
      {
        question: 'Can I upgrade or downgrade my plan?',
        answer: 'Yes, you can change your subscription plan at any time. When upgrading, you\'ll only be charged the prorated difference. When downgrading, your new rate will take effect at the next billing cycle.'
      }
    ]
  },
  {
    id: 'technical',
    name: 'Technical',
    icon: <Settings className="w-5 h-5" />,
    questions: [
      {
        question: 'How do I integrate TradingView with BOTINO.NET?',
        answer: 'You can easily connect TradingView alerts to BOTINO.NET through our webhook integration system. Simply create alerts in TradingView and set the webhook URL provided in your BOTINO.NET dashboard.'
      },
      {
        question: 'What is the platform uptime?',
        answer: 'Our platform maintains a 99.9% uptime guarantee. We have redundant systems and monitoring in place to ensure your trading bots operate reliably 24/7.'
      }
    ]
  },
  {
    id: 'trading-bots',
    name: 'Trading Bots',
    icon: <Bot className="w-5 h-5" />,
    questions: [
      {
        question: 'Do I need programming knowledge to use trading bots?',
        answer: 'No, BOTINO.NET is designed to be user-friendly for traders of all technical backgrounds. Our intuitive interface allows you to create, customize, and deploy trading bots without writing a single line of code.'
      },
      {
        question: 'Can I test my strategy before risking real money?',
        answer: 'Yes, BOTINO.NET offers backtesting and paper trading features that allow you to test your trading strategies using historical data or in real-time market conditions without risking actual funds.'
      }
    ]
  }
];

export const FAQPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState(categories[0].id);
  const [openQuestion, setOpenQuestion] = useState<number | null>(null);

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="pt-20">
      <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Find answers to common questions about BOTINO.NET and cryptocurrency trading bots.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Category Selection */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setSelectedCategory(category.id);
                setOpenQuestion(null);
              }}
              className={`px-6 py-3 rounded-md text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                selectedCategory === category.id
                  ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-lg scale-105'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md hover:scale-105 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {category.icon}
              {category.name}
            </button>
          ))}
        </div>

        {/* Questions for Selected Category */}
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl mx-auto"
        >
          {categories.find(cat => cat.id === selectedCategory)?.questions.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="mb-4"
            >
              <button
                onClick={() => toggleQuestion(index)}
                className={`w-full text-left bg-white dark:bg-gray-800 rounded-md p-6 shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-300 ${
                  openQuestion === index 
                    ? 'ring-2 ring-blue-500 dark:ring-blue-400' 
                    : 'hover:shadow-lg hover:scale-[1.02]'
                }`}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {faq.question}
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                      openQuestion === index ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                
                <AnimatePresence>
                  {openQuestion === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="mt-4 text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <CallToAction />
    </div>
  );
};